import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Animated } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';

export default function HeroMockupCarousel() {
  const { colors, isDark } = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);
  const [fadeAnim] = useState(new Animated.Value(1));
  const [typedText, setTypedText] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [cursorPosition, setCursorPosition] = useState({ x: 20, y: 20 });

  const mockups = [
    {
      title: 'Proposal Generator',
      description: 'AI-powered proposal writing',
      type: 'proposal',
      content: 'I need a full-stack developer to build a modern React Native mobile app with Firebase backend. The app should include user authentication, real-time data sync, and push notifications.',
    },
    {
      title: 'Task Manager',
      description: 'Organize work efficiently',
      type: 'tasks',
      tasks: [
        'Design user interface mockups',
        'Set up Firebase project',
        'Implement authentication flow',
        'Build real-time sync',
      ]
    },
    {
      title: 'Case Study Creator',
      description: 'Showcase your best work',
      type: 'casestudy',
      content: 'Built a productivity app that increased user engagement by 300% through intelligent task prioritization and AI-powered insights.',
    },
    {
      title: 'AI Assistant',
      description: 'Smart suggestions in real-time',
      type: 'assistant',
      suggestions: [
        'Add more technical details',
        'Mention your experience with React Native',
        'Include timeline estimates',
      ]
    },
  ];

  const currentMockup = mockups[activeIndex];

  // Typing animation
  useEffect(() => {
    if (currentMockup.type === 'proposal' || currentMockup.type === 'casestudy') {
      const fullText = currentMockup.content;
      let currentIndex = 0;
      setTypedText('');

      const typingInterval = setInterval(() => {
        if (currentIndex < fullText.length) {
          setTypedText(fullText.substring(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typingInterval);
        }
      }, 30);

      return () => clearInterval(typingInterval);
    }
  }, [activeIndex]);

  // Cursor blink animation
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setCursorVisible(prev => !prev);
    }, 500);

    return () => clearInterval(blinkInterval);
  }, []);

  // Cursor movement animation
  useEffect(() => {
    const moveInterval = setInterval(() => {
      setCursorPosition({
        x: Math.random() * 300 + 50,
        y: Math.random() * 200 + 80,
      });
    }, 2000);

    return () => clearInterval(moveInterval);
  }, []);

  // Auto-rotate slides
  useEffect(() => {
    const interval = setInterval(() => {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => {
        setActiveIndex((prev) => (prev + 1) % mockups.length);
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }).start();
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const renderContent = () => {
    switch (currentMockup.type) {
      case 'proposal':
        return (
          <View style={styles.proposalContent}>
            <View style={[styles.stepIndicator, { backgroundColor: colors.secondary }]}>
              <View style={[styles.stepDot, { backgroundColor: colors.accent }]} />
              <View style={[styles.stepDot, { backgroundColor: colors.accent, opacity: 0.5 }]} />
              <View style={[styles.stepDot, { backgroundColor: colors.border }]} />
            </View>
            <Text style={[styles.label, { color: colors.textSecondary }]}>Project Description</Text>
            <View style={[styles.textBox, { backgroundColor: colors.secondary, borderColor: colors.border }]}>
              <Text style={[styles.typedText, { color: colors.text }]}>
                {typedText}
                {cursorVisible && <Text style={styles.cursor}>|</Text>}
              </Text>
            </View>
            <View style={styles.buttonRow}>
              <View style={[styles.button, { backgroundColor: colors.secondary, borderColor: colors.border }]}>
                <Text style={[styles.buttonText, { color: colors.textSecondary }]}>Back</Text>
              </View>
              <View style={[styles.button, styles.primaryButton, { backgroundColor: colors.accent }]}>
                <Text style={[styles.buttonText, { color: '#fff' }]}>Generate Proposal</Text>
              </View>
            </View>
          </View>
        );

      case 'tasks':
        return (
          <View style={styles.tasksContent}>
            <Text style={[styles.sectionTitle, { color: colors.accent }]}>High Priority</Text>
            {currentMockup.tasks.map((task, idx) => (
              <Animated.View 
                key={idx}
                style={[
                  styles.taskItem,
                  { 
                    backgroundColor: colors.secondary, 
                    borderColor: colors.border,
                    opacity: fadeAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [0, 1],
                    }),
                    transform: [{
                      translateY: fadeAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [20, 0],
                      })
                    }]
                  }
                ]}
              >
                <View style={[styles.checkbox, idx < 2 && styles.checkboxChecked, { borderColor: colors.border, backgroundColor: idx < 2 ? colors.accent : 'transparent' }]}>
                  {idx < 2 && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={[styles.taskText, { color: colors.text, textDecorationLine: idx < 2 ? 'line-through' : 'none', opacity: idx < 2 ? 0.6 : 1 }]}>
                  {task}
                </Text>
              </Animated.View>
            ))}
          </View>
        );

      case 'casestudy':
        return (
          <View style={styles.caseStudyContent}>
            <Text style={[styles.label, { color: colors.textSecondary }]}>Project Results</Text>
            <View style={[styles.textBox, { backgroundColor: colors.secondary, borderColor: colors.border }]}>
              <Text style={[styles.typedText, { color: colors.text }]}>
                {typedText}
                {cursorVisible && <Text style={styles.cursor}>|</Text>}
              </Text>
            </View>
            <View style={styles.statsRow}>
              <View style={[styles.statCard, { backgroundColor: colors.secondary, borderColor: colors.accent }]}>
                <Text style={[styles.statValue, { color: colors.accent }]}>300%</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Engagement</Text>
              </View>
              <View style={[styles.statCard, { backgroundColor: colors.secondary, borderColor: colors.accent }]}>
                <Text style={[styles.statValue, { color: colors.accent }]}>10K+</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Users</Text>
              </View>
            </View>
          </View>
        );

      case 'assistant':
        return (
          <View style={styles.assistantContent}>
            <Text style={[styles.aiTitle, { color: colors.text }]}>AI Suggestions</Text>
            {currentMockup.suggestions.map((suggestion, idx) => (
              <Animated.View 
                key={idx}
                style={[
                  styles.suggestionCard,
                  { 
                    backgroundColor: colors.secondary, 
                    borderColor: colors.accent,
                    opacity: fadeAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [0, 1],
                    }),
                    transform: [{
                      translateX: fadeAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-20, 0],
                      })
                    }]
                  }
                ]}
              >
                <Text style={[styles.suggestionText, { color: colors.text }]}>{suggestion}</Text>
                <View style={styles.suggestionButtons}>
                  <View style={[styles.suggestionBtn, { backgroundColor: colors.accent }]}>
                    <Text style={[styles.suggestionBtnText, { color: '#fff' }]}>Apply</Text>
                  </View>
                  <View style={[styles.suggestionBtn, { backgroundColor: colors.secondary, borderColor: colors.border, borderWidth: 1 }]}>
                    <Text style={[styles.suggestionBtnText, { color: colors.textSecondary }]}>Ignore</Text>
                  </View>
                </View>
              </Animated.View>
            ))}
          </View>
        );

      default:
        return null;
    }
  };

  return (
    <View style={styles.mockupContent}>
      {/* Header */}
      <View style={[styles.mockupHeader, { backgroundColor: colors.secondary }]}>
        <View style={[styles.mockupDot, { backgroundColor: '#ff5f56' }]} />
        <View style={[styles.mockupDot, { backgroundColor: '#ffbd2e' }]} />
        <View style={[styles.mockupDot, { backgroundColor: '#27c93f' }]} />
        <View style={styles.mockupTitleContainer}>
          <Text style={[styles.mockupTitle, { color: colors.text }]}>{currentMockup.title}</Text>
          <Text style={[styles.mockupDescription, { color: colors.textSecondary }]}>{currentMockup.description}</Text>
        </View>
      </View>

      {/* Animated Content */}
      <Animated.View style={[styles.mockupBody, { backgroundColor: colors.primary, opacity: fadeAnim }]}>
        {renderContent()}
      </Animated.View>

      {/* Animated Cursor */}
      <Animated.View 
        style={[
          styles.animatedCursor,
          { 
            left: cursorPosition.x,
            top: cursorPosition.y,
            opacity: cursorVisible ? 1 : 0.7,
          }
        ]}
      >
        <View style={[styles.cursorPointer, { backgroundColor: isDark ? '#818cf8' : '#667eea' }]} />
      </Animated.View>

      {/* Dots Indicator */}
      <View style={styles.dotsContainer}>
        {mockups.map((_, idx) => (
          <View
            key={idx}
            style={[
              styles.dot,
              { backgroundColor: idx === activeIndex ? colors.accent : colors.border }
            ]}
          />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  mockupContent: {
    flex: 1,
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  mockupHeader: {
    flexDirection: 'row',
    gap: 8,
    padding: 16,
    alignItems: 'center',
  },
  mockupDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  mockupTitleContainer: {
    marginLeft: 12,
  },
  mockupTitle: {
    fontSize: 14,
    fontWeight: '700',
  },
  mockupDescription: {
    fontSize: 11,
    marginTop: 2,
  },
  mockupBody: {
    padding: 24,
    minHeight: 380,
  },
  // Proposal styles
  proposalContent: {
    gap: 16,
  },
  stepIndicator: {
    flexDirection: 'row',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
  },
  textBox: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    minHeight: 140,
  },
  typedText: {
    fontSize: 14,
    lineHeight: 20,
  },
  cursor: {
    fontSize: 14,
    fontWeight: '300',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  button: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
  },
  primaryButton: {
    borderWidth: 0,
  },
  buttonText: {
    fontSize: 13,
    fontWeight: '600',
  },
  // Tasks styles
  tasksContent: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  taskItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    gap: 12,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    borderWidth: 0,
  },
  checkmark: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  taskText: {
    fontSize: 14,
    flex: 1,
  },
  // Case study styles
  caseStudyContent: {
    gap: 16,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  statCard: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 28,
    fontWeight: '800',
  },
  statLabel: {
    fontSize: 12,
    marginTop: 4,
  },
  // Assistant styles
  assistantContent: {
    gap: 14,
  },
  aiTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  suggestionCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderLeftWidth: 4,
  },
  suggestionText: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 12,
  },
  suggestionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  suggestionBtn: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 6,
  },
  suggestionBtnText: {
    fontSize: 12,
    fontWeight: '600',
  },
  // Animated cursor
  animatedCursor: {
    position: 'absolute',
    width: 12,
    height: 12,
    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 10,
  },
  cursorPointer: {
    width: 12,
    height: 12,
    borderRadius: 6,
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
  },
  // Dots indicator
  dotsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    transition: 'all 0.3s ease',
  },
});
